<template>
  <div class="sequence-builder-container">
    <!-- Barra superior de control: Modo y Preview de la cadena generada -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-2 p-2 bg-light border rounded">
      <div class="d-flex align-items-center mb-1 mb-md-0">
        <span class="font-weight-bold small text-muted mr-2">
          <i class="bi bi-diagram-3 mr-1 text-primary"></i>Modo:
        </span>
        <div class="btn-group btn-group-sm" role="group">
          <button
            type="button"
            class="btn py-0 px-2"
            :class="mode === 'visual' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="setMode('visual')"
          >
            <i class="bi bi-grid mr-1"></i>Constructor Gráfico
          </button>
          <button
            type="button"
            class="btn py-0 px-2"
            :class="mode === 'text' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="setMode('text')"
          >
            <i class="bi bi-code mr-1"></i>Texto Directo
          </button>
        </div>
      </div>

      <!-- Cadena de secuencia resultante -->
      <div class="d-flex align-items-center">
        <span class="small text-muted mr-1 font-weight-bold">Secuencia:</span>
        <code class="px-2 py-1 rounded bg-white border font-weight-bold text-primary mr-1" style="font-size: 0.9rem;">
          {{ currentSequenceString || '(vacía)' }}
        </code>
        <button
          v-if="currentSequenceString"
          type="button"
          class="btn btn-outline-secondary btn-sm py-0 px-1"
          title="Copiar secuencia al portapapeles"
          @click="copySequenceToClipboard"
        >
          <i class="bi bi-clipboard"></i>
        </button>
      </div>
    </div>

    <!-- Advertencia cuando no hay integrador seleccionado -->
    <div v-if="!targetIntegradorId" class="alert alert-warning py-2 px-3 small mb-3">
      <i class="bi bi-exclamation-triangle-fill mr-1"></i>
      <strong>Atención:</strong> Debe seleccionar un integrador arriba para poder buscar y agregar sus Billing Codes a la secuencia.
    </div>

    <!-- MODO 1: CONSTRUCTOR GRÁFICO -->
    <div v-show="mode === 'visual'" class="visual-builder">
      <!-- Contenedor de Etapas (Ramas separadas por '-') -->
      <div
        v-for="(stage, sIndex) in stages"
        :key="sIndex"
        class="stage-block mb-3"
      >
        <div class="card border-primary shadow-sm">
          <!-- Cabecera de la Etapa -->
          <div class="card-header py-2 bg-light d-flex flex-wrap justify-content-between align-items-center">
            <div class="d-flex align-items-center flex-wrap">
              <span class="badge badge-primary mr-2" style="font-size: 0.85rem;">
                Etapa {{ sIndex + 1 }}
                <span v-if="stages.length === 1"> (Única)</span>
                <span v-else-if="sIndex === 0"> (Inicial)</span>
                <span v-else> (Transición)</span>
              </span>

              <!-- Configuración de ciclos / repeticiones de la etapa -->
              <div class="d-inline-flex align-items-center small ml-md-2 mt-1 mt-md-0">
                <span class="text-muted mr-1">Duración del ciclo:</span>
                <select
                  class="form-control form-control-sm py-0"
                  style="width: auto; height: 26px;"
                  :value="stage.cycles ? 'custom' : 'infinite'"
                  @change="onStageCyclesModeChange(sIndex, $event.target.value)"
                >
                  <option value="infinite">Indefinido / Estándar</option>
                  <option value="custom">Repetir N ciclos {N}</option>
                </select>

                <div v-if="stage.cycles" class="input-group input-group-sm ml-2" style="width: 130px; height: 26px;">
                  <input
                    type="number"
                    min="1"
                    class="form-control form-control-sm py-0"
                    placeholder="N° ciclos"
                    style="height: 26px;"
                    :value="stage.cycles"
                    @input="onStageCyclesInputChange(sIndex, $event.target.value)"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text py-0 px-1 font-weight-bold">{{ '{' + (stage.cycles || 'N') + '}' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Botón Eliminar Etapa -->
            <button
              v-if="stages.length > 1"
              type="button"
              class="btn btn-outline-danger btn-sm py-0 px-2 mt-1 mt-md-0"
              title="Eliminar esta etapa"
              @click="removeStage(sIndex)"
            >
              <i class="bi bi-trash mr-1"></i>Eliminar Etapa
            </button>
          </div>

          <!-- Cuerpo de la Etapa: Cascada de Cobro (Billing Codes separados por '/') -->
          <div class="card-body p-2 bg-white">
            <div class="small font-weight-bold text-muted mb-2 d-flex justify-content-between align-items-center">
              <span>
                <i class="bi bi-list-ol mr-1 text-primary"></i>
                Cascada de Cobro (orden de prioridad de intento):
              </span>
              <span class="badge badge-light border">
                {{ stage.codes.length }} código(s) en cascada
              </span>
            </div>

            <!-- Lista de Códigos de la Etapa -->
            <div v-if="stage.codes.length > 0" class="cascade-list">
              <div
                v-for="(codeId, cIndex) in stage.codes"
                :key="codeId + '_' + cIndex"
                class="cascade-item-wrapper"
              >
                <!-- Indicador de flecha entre códigos de cascada -->
                <div v-if="cIndex > 0" class="cascade-arrow my-1 text-center text-muted small font-italic">
                  <i class="bi bi-arrow-down-short text-warning font-weight-bold" style="font-size: 1.1rem;"></i>
                  <span>Si falla el intento anterior, cobrar:</span>
                </div>

                <!-- Tarjeta del Billing Code -->
                <div class="d-flex align-items-center justify-content-between p-2 rounded border bg-light">
                  <div class="d-flex align-items-center flex-wrap mr-2">
                    <span class="text-muted mr-2 font-weight-bold">
                      {{ cIndex + 1 }}° Intento
                    </span>
                    <span class="font-weight-bold mr-2">ID: {{ codeId }}</span>

                    <!-- Datos del catálogo si está cargado -->
                    <template v-if="catalogMap[codeId]">
                      <!-- Código TAS como texto plano -->
                      <span class="mr-2">
                        TAS: {{ catalogMap[codeId].cod_tas || '-' }}
                      </span>
                      <!-- Descripción como texto plano -->
                      <span class="mr-2 text-muted">
                        {{ catalogMap[codeId].descripcion || '-' }}
                      </span>
                      <!-- Precios del Billing Code como texto plano sin colores -->
                      <span class="mr-2 font-weight-bold">
                        Precio: {{ formatMoney(catalogMap[codeId].precio_sin_imp || catalogMap[codeId].full_price) }}
                      </span>
                      <span class="text-muted small">
                        (c/imp: {{ formatMoney(catalogMap[codeId].precio_con_imp || catalogMap[codeId].full_price_tax) }})
                      </span>
                    </template>
                    <template v-else>
                      <span class="text-muted small font-italic mr-2">
                        Billing Code {{ codeId }}
                      </span>
                      <span class="text-muted small">
                        (Consultando datos...)
                      </span>
                    </template>
                  </div>

                  <!-- Botones de Acción de Cascada (Mover arriba / abajo / eliminar) -->
                  <div class="btn-group btn-group-sm flex-shrink-0" role="group">
                    <button
                      type="button"
                      class="btn btn-outline-secondary py-0 px-1"
                      title="Mover arriba en la prioridad"
                      :disabled="cIndex === 0"
                      @click="moveCodeUp(sIndex, cIndex)"
                    >
                      <i class="bi bi-arrow-up"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary py-0 px-1"
                      title="Mover abajo en la prioridad"
                      :disabled="cIndex === stage.codes.length - 1"
                      @click="moveCodeDown(sIndex, cIndex)"
                    >
                      <i class="bi bi-arrow-down"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-danger py-0 px-1"
                      title="Quitar este código de la cascada"
                      @click="removeCodeFromStage(sIndex, cIndex)"
                    >
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Estado vacío de la etapa -->
            <div v-else class="alert alert-light border text-center text-muted py-2 mb-2 small">
              <i class="bi bi-info-circle mr-1"></i>
              Aún no hay códigos en esta etapa. Haga clic en el botón inferior para agregar el primer Billing Code.
            </div>

            <!-- Botón Agregar Código a la Cascada de esta etapa -->
            <div class="mt-2 text-right">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                :disabled="!targetIntegradorId"
                @click="openCodeSelector(sIndex)"
              >
                <i class="bi bi-plus-circle mr-1"></i>
                Agregar Billing Code a la Cascada ( / )
              </button>
            </div>
          </div>
        </div>

        <!-- Conector visual entre etapas sucesivas (-) -->
        <div v-if="sIndex < stages.length - 1" class="transition-separator my-2 text-center">
          <div class="badge badge-warning text-dark py-1 px-3 shadow-sm border border-secondary">
            <i class="bi bi-arrow-down-circle-fill mr-1"></i>
            Transición a siguiente etapa tras agotar los {{ stage.cycles || 'N' }} ciclos ( - )
          </div>
        </div>
      </div>

      <!-- Barra de acciones del constructor -->
      <div class="d-flex flex-wrap justify-content-between align-items-center mt-2">
        <button
          type="button"
          class="btn btn-sm btn-outline-success"
          @click="addNewStage"
        >
          <i class="bi bi-plus-lg mr-1"></i>
          Agregar Nueva Etapa de Transición ( - )
        </button>

        <button
          v-if="hasAtLeastOneCode"
          type="button"
          class="btn btn-sm btn-info ml-auto mt-1 mt-md-0"
          title="Autocompletar Precios (Full Price y Min Price) en el formulario en base a los códigos seleccionados"
          @click="autoSuggestPrices"
        >
          <i class="bi bi-calculator mr-1"></i>
          Sugerir Precios desde la Secuencia
        </button>
      </div>
    </div>

    <!-- MODO 2: TEXTO DIRECTO -->
    <div v-show="mode === 'text'" class="text-mode-container">
      <div class="form-group mb-1">
        <label class="font-weight-bold small text-muted mb-1">
          Cadena de Secuencia Manual:
        </label>
        <input
          type="text"
          class="form-control"
          placeholder="Ej: 582/583/667{27}-1309/583/667"
          v-model="rawTextInput"
          @keydown="onSequenceKeyDown"
          @input="onRawTextInputChange"
        />
        <small class="form-text text-muted">
          Use <code>/</code> para separar cascada de intentos, <code>{N}</code> para repeticiones de ciclo y <code>-</code> para transiciones.
        </small>
      </div>
    </div>

    <!-- SUBMODAL: SELECTOR / BUSCADOR DE BILLING CODES DEL INTEGRADOR -->
    <crud-modal
      :show="showCodeModal"
      title="Seleccionar Billing Code"
      size="lg"
      :show-save="false"
      cancel-text="Cerrar"
      nested
      @close="closeCodeModal"
    >
      <div class="code-selector-content">
        <!-- Indicador de Integrador -->
        <div class="mb-3 d-flex flex-wrap align-items-center justify-content-between p-2 rounded bg-light border">
          <div>
            <span class="text-muted small font-weight-bold">Integrador:</span>
            <strong class="ml-1 text-primary">{{ resolvedIntegradorNombre }}</strong>
            <span class="badge badge-secondary ml-2">ID: {{ targetIntegradorId }}</span>
          </div>
          <div>
            <span class="badge badge-info">
              Destino: Etapa {{ targetStageIndex + 1 }}
            </span>
          </div>
        </div>

        <!-- Buscador en tiempo real -->
        <div class="input-group input-group-sm mb-2">
          <div class="input-group-prepend">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
          </div>
          <input
            type="text"
            class="form-control"
            placeholder="Buscar por código TAS, descripción o ID..."
            v-model="searchCodeQuery"
          />
          <div class="input-group-append" v-if="searchCodeQuery">
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="searchCodeQuery = ''"
              title="Limpiar búsqueda"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>

        <!-- Spinner cargando códigos -->
        <div v-if="loadingCodes" class="alert alert-light border text-center py-4 my-2">
          <div class="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
          <span class="text-muted small font-weight-bold">Cargando Billing Codes del integrador...</span>
        </div>

        <!-- Tabla de Billing Codes filtrados -->
        <div
          v-else-if="filteredCodes.length > 0"
          class="table-responsive border rounded"
          style="max-height: 320px; overflow-y: auto;"
        >
          <table class="table table-sm table-hover table-striped mb-0">
            <thead class="thead-light sticky-top">
              <tr>
                <th scope="col" style="width: 70px;" class="text-center">ID</th>
                <th scope="col">Código TAS</th>
                <th scope="col">Descripción</th>
                <th scope="col" class="text-right">Precio s/ Imp.</th>
                <th scope="col" class="text-right">Precio c/ Imp.</th>
                <th scope="col" style="width: 110px;" class="text-center">Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredCodes" :key="item.id_billing_code">
                <td class="text-center align-middle font-weight-bold">
                  {{ item.id_billing_code }}
                </td>
                <td class="align-middle">
                  {{ item.cod_tas || '-' }}
                </td>
                <td class="align-middle">
                  {{ item.descripcion || '-' }}
                </td>
                <td class="text-right align-middle">
                  {{ formatMoney(item.precio_sin_imp || item.full_price) }}
                </td>
                <td class="text-right align-middle">
                  {{ formatMoney(item.precio_con_imp || item.full_price_tax) }}
                </td>
                <td class="text-center align-middle">
                  <button
                    type="button"
                    class="btn btn-sm btn-primary py-0 px-2"
                    @click="addCodeToTargetStage(item)"
                  >
                    <i class="bi bi-plus mr-1"></i>Agregar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Estado vacío si el integrador no tiene códigos -->
        <div
          v-else-if="!loadingCodes && billingCodes.length === 0"
          class="alert alert-warning text-center my-3 py-3"
        >
          <i class="bi bi-exclamation-triangle mr-1"></i>
          No se encontraron Billing Codes configurados para el integrador
          <strong>{{ resolvedIntegradorNombre }}</strong>.
        </div>

        <!-- Estado vacío por búsqueda -->
        <div
          v-else-if="!loadingCodes && billingCodes.length > 0 && filteredCodes.length === 0"
          class="alert alert-light border text-center text-muted my-3 py-3"
        >
          <i class="bi bi-info-circle mr-1"></i>
          No se encontraron códigos que coincidan con "{{ searchCodeQuery }}".
        </div>
      </div>
    </crud-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import CrudModal from "../utiles/CrudModal.vue";

export default {
  name: "SequenceBuilder",
  components: {
    CrudModal,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    idIntegrador: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      mode: "visual", // 'visual' | 'text'
      rawTextInput: "",
      stages: [
        {
          cycles: null,
          codes: [],
        },
      ],
      billingCodes: [],
      catalogMap: {},
      loadingCodes: false,
      showCodeModal: false,
      targetStageIndex: 0,
      searchCodeQuery: "",
      isInternalSync: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("integrador", ["integradores"]),

    targetIntegradorId() {
      if (
        this.idIntegrador !== null &&
        this.idIntegrador !== undefined &&
        this.idIntegrador !== "" &&
        this.idIntegrador !== "todos"
      ) {
        return this.idIntegrador;
      }
      return null;
    },

    integradoresList() {
      let list = [];
      if (this.integradores && this.integradores.data && this.integradores.data.length > 0) {
        list = [...this.integradores.data];
      } else if (this.user && this.user.integradores && Array.isArray(this.user.integradores)) {
        list = [...this.user.integradores];
      }
      return list;
    },

    resolvedIntegradorNombre() {
      const id = this.targetIntegradorId;
      if (!id) return "";
      const found = this.integradoresList.find((i) => (i.id_integrador || i.id) == id);
      return found ? found.integrador : String(id);
    },

    currentSequenceString() {
      return this.generateSequenceFromStages(this.stages);
    },

    hasAtLeastOneCode() {
      return this.stages.some((s) => s.codes && s.codes.length > 0);
    },

    filteredCodes() {
      if (!this.billingCodes || !Array.isArray(this.billingCodes)) return [];
      if (!this.searchCodeQuery || !this.searchCodeQuery.trim()) {
        return this.billingCodes;
      }
      const q = this.searchCodeQuery.trim().toLowerCase();
      return this.billingCodes.filter((bc) => {
        const id = String(bc.id_billing_code || "");
        const tas = String(bc.cod_tas || "").toLowerCase();
        const desc = String(bc.descripcion || "").toLowerCase();
        return id.includes(q) || tas.includes(q) || desc.includes(q);
      });
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (this.isInternalSync) return;
        this.rawTextInput = newVal || "";
        this.stages = this.parseSequenceToStages(newVal);
        this.fetchMissingCodes();
      },
    },
    targetIntegradorId: {
      immediate: true,
      handler(newInteg) {
        if (newInteg) {
          this.fetchBillingCodesForIntegrador(newInteg);
        } else {
          this.billingCodes = [];
          this.fetchMissingCodes();
        }
      },
    },
  },
  methods: {
    setMode(newMode) {
      this.mode = newMode;
      if (newMode === "text") {
        this.rawTextInput = this.currentSequenceString;
      } else {
        this.stages = this.parseSequenceToStages(this.rawTextInput);
        this.emitSequenceChange();
        this.fetchMissingCodes();
      }
    },

    parseSequenceToStages(seq) {
      if (!seq || typeof seq !== "string" || !seq.trim()) {
        return [{ cycles: null, codes: [] }];
      }
      const stages = [];
      const sanitized = seq.replace(/[^0-9/{}-]/g, "");
      const parts = sanitized.trim().split("-");

      for (const part of parts) {
        if (!part.trim()) continue;
        const match = part.match(/{(\d+)}/);
        const cycles = match ? parseInt(match[1], 10) : null;
        const cleanPart = part.replace(/{\d*}/g, "");
        const codes = cleanPart
          .split("/")
          .map((c) => c.replace(/[^0-9]/g, "").trim())
          .filter((c) => c.length > 0);

        stages.append ? null : stages.push({
          cycles: cycles && cycles > 0 ? cycles : null,
          codes: codes,
        });
      }

      return stages.length > 0 ? stages : [{ cycles: null, codes: [] }];
    },

    onSequenceKeyDown(e) {
      const allowedControlKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];
      if (
        allowedControlKeys.includes(e.key) ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey
      ) {
        return;
      }
      if (!/^[0-9/{}-]$/.test(e.key)) {
        e.preventDefault();
      }
    },

    generateSequenceFromStages(stages) {
      if (!stages || !Array.isArray(stages)) return "";
      const parts = [];

      for (const stage of stages) {
        const validCodes = (stage.codes || [])
          .map((c) => String(c).trim())
          .filter((c) => c.length > 0);

        if (validCodes.length === 0) continue;
        let partStr = validCodes.join("/");
        if (stage.cycles && parseInt(stage.cycles, 10) > 0) {
          partStr += `{${parseInt(stage.cycles, 10)}}`;
        }
        parts.push(partStr);
      }

      return parts.join("-");
    },

    emitSequenceChange() {
      const generated = this.generateSequenceFromStages(this.stages);
      this.isInternalSync = true;
      this.$emit("input", generated);
      this.$nextTick(() => {
        this.isInternalSync = false;
      });
    },

    onRawTextInputChange(e) {
      const raw = e && e.target ? e.target.value : this.rawTextInput || "";
      const cleaned = String(raw).replace(/[^0-9/{}-]/g, "");
      if (cleaned !== raw && e && e.target) {
        e.target.value = cleaned;
      }
      this.rawTextInput = cleaned;
      this.isInternalSync = true;
      this.$emit("input", this.rawTextInput);
      this.stages = this.parseSequenceToStages(this.rawTextInput);
      this.$nextTick(() => {
        this.isInternalSync = false;
      });
    },

    onStageCyclesModeChange(stageIndex, mode) {
      if (mode === "infinite") {
        this.stages[stageIndex].cycles = null;
      } else {
        this.stages[stageIndex].cycles = this.stages[stageIndex].cycles || 1;
      }
      this.emitSequenceChange();
    },

    onStageCyclesInputChange(stageIndex, val) {
      const parsed = parseInt(val, 10);
      this.stages[stageIndex].cycles = isNaN(parsed) || parsed <= 0 ? null : parsed;
      this.emitSequenceChange();
    },

    addNewStage() {
      this.stages.push({
        cycles: null,
        codes: [],
      });
      this.emitSequenceChange();
    },

    removeStage(stageIndex) {
      if (this.stages.length <= 1) return;
      this.stages.splice(stageIndex, 1);
      this.emitSequenceChange();
    },

    moveCodeUp(stageIndex, codeIndex) {
      if (codeIndex <= 0) return;
      const codes = this.stages[stageIndex].codes;
      const temp = codes[codeIndex];
      codes.splice(codeIndex, 1);
      codes.splice(codeIndex - 1, 0, temp);
      this.emitSequenceChange();
    },

    moveCodeDown(stageIndex, codeIndex) {
      const codes = this.stages[stageIndex].codes;
      if (codeIndex >= codes.length - 1) return;
      const temp = codes[codeIndex];
      codes.splice(codeIndex, 1);
      codes.splice(codeIndex + 1, 0, temp);
      this.emitSequenceChange();
    },

    removeCodeFromStage(stageIndex, codeIndex) {
      this.stages[stageIndex].codes.splice(codeIndex, 1);
      this.emitSequenceChange();
    },

    openCodeSelector(stageIndex) {
      this.targetStageIndex = stageIndex;
      this.searchCodeQuery = "";
      this.showCodeModal = true;
    },

    closeCodeModal() {
      this.showCodeModal = false;
    },

    addCodeToTargetStage(item) {
      const id = String(item.id_billing_code);
      const stage = this.stages[this.targetStageIndex];
      if (!stage.codes) {
        this.$set(stage, "codes", []);
      }
      stage.codes.push(id);
      this.$set(this.catalogMap, id, item);
      this.emitSequenceChange();

      // Feedback sutil
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `Código ${item.cod_tas || id} agregado a Etapa ${this.targetStageIndex + 1}`,
        showConfirmButton: false,
        timer: 1500,
      });
    },

    fetchBillingCodesForIntegrador(integradorId) {
      this.loadingCodes = true;
      axios
        .post(process.env.VUE_APP_API_URL + "billingcode/list", {
          id_integrador: integradorId,
        })
        .then((response) => {
          const list =
            response && response.data
              ? response.data.data || response.data
              : [];
          this.billingCodes = Array.isArray(list) ? list : [];
          for (const bc of this.billingCodes) {
            this.$set(this.catalogMap, String(bc.id_billing_code), bc);
          }
          this.fetchMissingCodes();
        })
        .catch(() => {
          this.billingCodes = [];
          this.fetchMissingCodes();
        })
        .finally(() => {
          this.loadingCodes = false;
        });
    },

    fetchMissingCodes() {
      const neededIds = [];
      if (this.stages && Array.isArray(this.stages)) {
        for (const stage of this.stages) {
          if (stage.codes && Array.isArray(stage.codes)) {
            for (const cid of stage.codes) {
              const strId = String(cid).trim();
              if (strId && !this.catalogMap[strId] && !neededIds.includes(strId)) {
                neededIds.push(strId);
              }
            }
          }
        }
      }
      if (neededIds.length === 0) return;

      axios
        .post(process.env.VUE_APP_API_URL + "billingcode/list", {
          ids: neededIds,
        })
        .then((response) => {
          const list =
            response && response.data
              ? response.data.data || response.data
              : [];
          if (Array.isArray(list) && list.length > 0) {
            for (const item of list) {
              this.$set(this.catalogMap, String(item.id_billing_code), item);
            }
          }
        })
        .catch(() => {});
    },

    autoSuggestPrices() {
      if (!this.stages || this.stages.length === 0) return;
      const firstStageCodes = this.stages[0].codes || [];
      if (firstStageCodes.length === 0) return;

      const primaryCodeId = firstStageCodes[0];
      const primaryCode = this.catalogMap[primaryCodeId];

      let fullPrice = primaryCode ? primaryCode.precio_sin_imp || primaryCode.full_price : null;
      let fullPriceTax = primaryCode ? primaryCode.precio_con_imp || primaryCode.full_price_tax : null;

      // Calcular precio mínimo entre todos los códigos de la primera etapa
      let minPrice = fullPrice;
      for (const cid of firstStageCodes) {
        const item = this.catalogMap[cid];
        if (item) {
          const p = Number(item.precio_sin_imp || item.full_price);
          if (!isNaN(p)) {
            if (minPrice === null || p < Number(minPrice)) {
              minPrice = p;
            }
          }
        }
      }

      this.$emit("suggest-prices", {
        full_price: fullPrice !== null ? String(fullPrice) : "",
        full_price_tax: fullPriceTax !== null ? String(fullPriceTax) : "",
        min_price_bs: minPrice !== null ? String(minPrice) : "0",
      });

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Precios calculados y aplicados al formulario",
        showConfirmButton: false,
        timer: 1800,
      });
    },

    copySequenceToClipboard() {
      if (!this.currentSequenceString) return;
      navigator.clipboard.writeText(this.currentSequenceString).then(() => {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Secuencia copiada al portapapeles",
          showConfirmButton: false,
          timer: 1200,
        });
      });
    },

    formatMoney(val) {
      if (val === null || val === undefined || val === "") return "-";
      const cleaned = String(val).replace(/^\$\s*/, "").trim();
      const num = Number(cleaned);
      if (isNaN(num)) return cleaned;
      return num.toFixed(2);
    },
  },
};
</script>

<style scoped>
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}
.cascade-item-wrapper:first-child .cascade-arrow {
  display: none;
}
</style>
